import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';

const ShippingSettingsContext = createContext();

const DEFAULT_FEE = 4.99;
const DEFAULT_THRESHOLD = 100;

export const useShippingSettings = () => {
  const context = useContext(ShippingSettingsContext);
  if (!context) {
    throw new Error('useShippingSettings must be used within ShippingSettingsProvider');
  }
  return context;
};

/**
 * Compute shipping cost from subtotal (after discounts) using current config.
 * @param {number} subtotal
 * @param {{ shippingFee: number, freeShippingThreshold: number }} config
 * @returns {number}
 */
export function computeShipping(subtotal, config) {
  if (!config) return subtotal >= DEFAULT_THRESHOLD ? 0 : DEFAULT_FEE;
  const { shippingFee, freeShippingThreshold } = config;
  if (freeShippingThreshold > 0 && subtotal >= freeShippingThreshold) return 0;
  return shippingFee;
}

async function fetchShippingSettings() {
  const res = await api.get('/settings/shipping');
  return res.data;
}

export const ShippingSettingsProvider = ({ children }) => {
  const { data, isPending, isError } = useQuery({
    queryKey: ['settings', 'shipping'],
    queryFn: fetchShippingSettings,
    staleTime: 5 * 60 * 1000,
  });

  const config = useMemo(() => {
    if (isError || !data) {
      return { shippingFee: DEFAULT_FEE, freeShippingThreshold: DEFAULT_THRESHOLD };
    }
    return {
      shippingFee: typeof data.shippingFee === 'number' ? data.shippingFee : DEFAULT_FEE,
      freeShippingThreshold:
        typeof data.freeShippingThreshold === 'number' ? data.freeShippingThreshold : DEFAULT_THRESHOLD,
    };
  }, [data, isError]);

  const value = useMemo(
    () => ({
      ...config,
      loading: isPending,
      computeShipping: (subtotal) => computeShipping(subtotal, config),
    }),
    [config, isPending]
  );

  return (
    <ShippingSettingsContext.Provider value={value}>
      {children}
    </ShippingSettingsContext.Provider>
  );
};
